/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  addEmailRestrictions,
  getEmailRestrictions,
  updateUser,
} from "../../../../api/User";
import { UserAtom } from "../../../../atoms/UserAtom";
import { UserEmailRestrictionsAtom } from "../../../../atoms/UserEmailRestrictionsAtom";
import { customTooltip } from "../../../../styles/general";
import CountryFinderInput from "../../../CountryFinder";
import { TooltipText } from "../../../SideBar";
import StyledButton from "../../../StyledButton";
import BlackWhiteListToggle from "./BlackWhiteListToggle";

interface CountryProps {
  disabled?: boolean;
}

export default function BlockCountry(props: CountryProps) {
  const { disabled } = props;

  const [country, setCountry] = useState("US");
  const [loading, setLoading] = useState(false);
  const setEmailRestrictions = useSetRecoilState(UserEmailRestrictionsAtom);
  const [user, setUser] = useRecoilState(UserAtom);

  async function handleBlockCountry() {
    setLoading(true);
    getEmailRestrictions();

    try {
      if (user.options.countriesList === "BLACKLIST") {
        await addEmailRestrictions({
          countriesBlocked: [country.toUpperCase()],
        });
        toast("Country blocked", { type: "success" });
      } else {
        await addEmailRestrictions({
          countriesWhitelisted: [country.toUpperCase()],
        });
        toast("Country allowed", { type: "success" });
      }

      const emailRestrictions = await getEmailRestrictions();
      setEmailRestrictions(emailRestrictions);
      setUser((u) => ({ ...u, emailRestrictions }));
    } catch {
      toast("Error", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleChangeCountry(
    selectedCountry: "BLACKLIST" | "WHITELIST"
  ) {
    try {
      await updateUser({
        options: { ...user.options, countriesList: selectedCountry },
      });
      setUser({
        ...user,
        options: { ...user.options, countriesList: selectedCountry },
      });
    } catch {
      toast("Error", { type: "error" });
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleBlockCountry();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [country, user.options.countriesList]);

  return (
    <>
      <div data-tip data-for={"whitelist-blocklist-toggle"} className="title">
        <BlackWhiteListToggle
          disabled={disabled}
          selectedBlockCountryType={user.options.countriesList}
          handleChangeCountry={handleChangeCountry}
        />
      </div>

      {disabled && (
        <ReactTooltip
          id={"whitelist-blocklist-toggle"}
          place="bottom"
          effect="solid"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText style={{ textAlign: "center" }}>
            {
              "You may only enter countries in either the Whitelist or Blacklist sections.\nTo change options, please delete your current entries before proceeding."
            }
          </TooltipText>
        </ReactTooltip>
      )}

      <CountryFinderInput
        style={{
          height: "40px",
          borderColor: "#eceeef",
          fontSize: "16px",
        }}
        setKey={() => {}}
        handleChangeCountry={(a) => {
          setCountry(a.key);
        }}
      />

      <StyledButton
        css={css`
          width: 112px;
          height: 40px;
        `}
        onClick={handleBlockCountry}
      >
        {user.options.countriesList === "BLACKLIST" ? "Block" : "Allow"}
      </StyledButton>
    </>
  );
}
